import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { LandsbankinnContext } from '@landsbankinn/utils';
import React, { useContext } from 'react';
import type {
  fetchBonds,
  fetchBondsVariables,
  fetchBonds_bonds_nodes,
} from '../../../../graphql/__generated__/fetchBonds';
import { FETCH_BONDS } from '../../../../graphql/fetchBonds';
import { DataTable } from '../../../table/DataTable';
import {
  MarketsBodyBold,
  StickyFirstColumn,
} from '../../shared/MarketsComponents';
import {
  MarketsFormatNumber,
  NumberType,
} from '../../shared/MarketsFormatNumber';
import res from '../Bonds.res';

const StickyFirstTableContainer = styled(StickyFirstColumn)`
  div:first-of-type {
    table {
      margin-bottom: 60px;
    }
  }
`;

export const defaultBondVariables = {
  filter: [
    'HFF',
    'LSS0',
    'LSS1',
    'LSS2',
    'LSS3',
    'LSS4',
    'LSS5',
    'LSS6',
    'LSS7',
    'LSS8',
    'LSS9',
    'RIKS',
    'RIKV',
    'RIKB',
    'LBANK CBI',
    'LBANK CB ',
  ],
  first: 100,
};

interface IProps {
  bonds?: fetchBonds;
  prefix: string;
}

export function BondsMarketTable({ bonds, prefix }: IProps) {
  const { r } = useResource();
  const { client } = useContext(LandsbankinnContext);
  const { data = bonds } = useQuery<fetchBonds, fetchBondsVariables>(
    FETCH_BONDS,
    {
      client,
      variables: defaultBondVariables,
    },
  );

  const columns = React.useCallback(
    (title: string) => [
      {
        Header: title,
        accessor: 'symbol', // accessor is the "key" in the data
        headerStyle: {
          flexDirection: 'row-reverse',
        },
        Cell: ({ value }: any) => (
          <MarketsBodyBold noWrap={true}>{value}</MarketsBodyBold>
        ),
      },
      {
        Header: () => r(res.yield),
        sortType: 'basic',
        accessor: 'lastYield',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Percentage}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.price),
        accessor: 'last',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.priceChange),
        sortType: 'basic',
        accessor: 'intradayPerChange',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Percentage}
            decimalScale={2}
            displayPosNegColor
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.bidYield),
        sortType: 'basic',
        accessor: 'bidYield',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Percentage}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.bidPrice),
        accessor: 'bid',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.askPrice),
        accessor: 'ask',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.askYield),
        sortType: 'basic',
        accessor: 'askYield',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Percentage}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.numberOfTrades),
        accessor: 'intradayNumberOfTrades',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={0}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.turnover),
        accessor: 'intradayAccumulatedTurnover',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={0}
            number={value / 1000}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.duration),
        accessor: 'lastDuration',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
      },
    ],
    [],
  );

  const tableData = React.useMemo(() => {
    return data?.bonds.nodes.filter(
      (s) => s.lastDuration && s.lastDuration > 0,
    );
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <StickyFirstTableContainer>
      <DataTable<fetchBonds_bonds_nodes>
        aria-label={r(res.indexed)}
        data={
          tableData?.filter(
            (bond) =>
              bond.lastDuration &&
              bond.lastDuration > 0 &&
              bond.details?.isLinkedToIcelandicCPI === true,
          ) ?? []
        }
        columns={columns(r(res.indexed)) as any}
        colorTheme="primary"
        rowLink={({ original: bond }) => ({
          href: `${prefix}/[symbol]`,
          as: `${prefix}/${bond.symbol.split(' ').join('_').toLowerCase()}`,
        })}
        isClickable
      />
      <DataTable<fetchBonds_bonds_nodes>
        aria-label={r(res.nonIndexed)}
        data={
          tableData?.filter(
            (bond) =>
              bond.lastDuration &&
              bond.lastDuration > 0 &&
              bond.details?.isLinkedToIcelandicCPI === false,
          ) ?? []
        }
        columns={columns(r(res.nonIndexed)) as any}
        colorTheme="primary"
        rowLink={({ original: bond }) => ({
          href: `${prefix}/[symbol]`,
          as: `${prefix}/${bond.symbol.split(' ').join('_').toLowerCase()}`,
        })}
        isClickable
      />
    </StickyFirstTableContainer>
  );
}
